import { AlertCircle, Clock } from 'lucide-react';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Soft-pilot banner. Shows during the last 7 days of pilot grace, and
 * persistently after expiry. Reads still work; ModuleGate / hasModule
 * handle the actual write-blocking once `inPilotGrace` flips false.
 */
export function PilotStatusBanner() {
  const { user, userRole } = useAuth();
  const { pilotDaysRemaining, inPilotGrace, baseSubscription, isPilotExempt } = useEntitlements();

  if (!user) return null;
  // Only super_admins and program_directors see the pilot banner.
  if (userRole !== 'super_admin' && userRole !== 'program_director') return null;
  // Internal demo / pilot-exempt orgs never see the banner.
  if (isPilotExempt) return null;
  // Anyone with an active live base subscription has graduated past the pilot.
  if (baseSubscription?.status === 'active' || baseSubscription?.status === 'trialing') {
    return null;
  }
  if (pilotDaysRemaining === null) return null;

  // Only nudge inside last 7 days, or after expiry.
  if (inPilotGrace && pilotDaysRemaining > 7) return null;

  const isSuperAdmin = false;
  const expired = !inPilotGrace;
  const cls = expired
    ? 'bg-amber-100/70 border-amber-300 text-amber-900'
    : 'bg-blue-50/70 border-blue-200 text-blue-900';

  return (
    <div
      role="status"
      className={`w-full border-b px-4 py-2 text-sm flex items-center justify-center gap-2 ${cls}`}
    >
      {expired ? <AlertCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
      <span>
        {expired
          ? 'Pilot period has ended. Subscribe to keep using paid modules.'
          : `Pilot ends in ${pilotDaysRemaining} day${pilotDaysRemaining === 1 ? '' : 's'}.`}
        {isSuperAdmin && (
          <>
            {' '}
            <a className="underline font-medium" href="/?tab=billing">
              Open billing
            </a>
            .
          </>
        )}
      </span>
    </div>
  );
}
